import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { ChoiceGroup } from '@fluentui/react/lib/ChoiceGroup';
import { Label } from '@fluentui/react/lib/Label';
import PropTypes from 'prop-types';
import { partial, isEmpty, findKey, debounce, map } from 'underscore';

import {
  SmartTextEditor,
} from '@bingads-webui-campaign-react/smart-component-text';
import {
  useSiteConfig,
  useInstrumentationServiceContext,
  useEditorStatusContext,
  editorStatusLoadingWrapperForPromise,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  useDefaultEditorStyle,
  getWebsiteSourceDataByKey,
  WEBSITE_SOURCE_DATA_KEY_MAPPING,
  truncateStringEndWithEmoji,
} from '@bingads-webui-campaign-react/smart-component-util';

import {
  dataProperties,
  videoSizeOption,
  VideoSize,
  VideoSize2Px,
  KeyBoardTypingDebounceTime,
  DescriptionHardLimit
} from './data-config';
import { getErrors } from './errors';
import { isVideoAvailable } from './video-helper';

export const SmartVideoEditor = ({
  i18n,
}) => {
  const {
    titleClassName,
    descriptionClassName,
    editorContentClassName,
    sectionTitleClassName,
    choiceGroupClassName,
  } = useDefaultEditorStyle();

  const { siteConfig, dispatchSiteConfig } = useSiteConfig();
  const logger = useInstrumentationServiceContext();
  const [, dispatchEditorStatus] = useEditorStatusContext();
  const componentKey = WEBSITE_SOURCE_DATA_KEY_MAPPING.EmbeddedVideos;
  const componentConfig = getWebsiteSourceDataByKey(siteConfig, componentKey);
  // Now we only support 1 video.
  const videoConfigRef = useRef();
  videoConfigRef.current = (!isEmpty(componentConfig) && componentConfig[0]) || {};
  const [realtimeInputUrl, setRealtimeInputUrl] =
    useState(videoConfigRef.current && videoConfigRef.current[dataProperties.videoUrl]);
  const [videoCanPlay, setVideoCanPlay] =
    useState(videoConfigRef.current && videoConfigRef.current[dataProperties.videoCanPlay]);

  const updateSiteConfig = useCallback((overwriteConfig) => {
    videoConfigRef.current = {
      ...videoConfigRef.current,
      ...overwriteConfig,
    };

    dispatchSiteConfig({
      type: 'UPDATE_WEBSITESOURCEDATA_BY_NAME',
      payload: {
        name: componentKey,
        value: [videoConfigRef.current],
      },
    });
  }, [componentKey, dispatchSiteConfig]);

  const updateVideoStatus = useCallback((url, isValid) => {
    const overwriteConfig = {
      [dataProperties.videoUrl]: url,
      [dataProperties.videoCanPlay]: isValid,
    };

    updateSiteConfig(overwriteConfig);
  }, [updateSiteConfig]);

  const checkVideoStatus = useCallback((url) => {
    const previousUrl = isEmpty(videoConfigRef.current) ? '' :
      videoConfigRef.current[dataProperties.videoUrl];

    logger.logTrace('SmartPageEditor|Video|User input new url', url);

    if (previousUrl !== url) {
      return isVideoAvailable(url)
        .then((res) => {
          updateVideoStatus(url, res);
          setVideoCanPlay(res);
        })
        .catch((err) => {
          logger.logTrace('SmartPageEditor|Video|Failed to query video status', err);
          setVideoCanPlay(false);
        });
    }

    return Promise.resolve(null);
  }, [updateVideoStatus, logger]);

  const debouncedCheckVideoStatus = useMemo(
    () => debounce(checkVideoStatus, KeyBoardTypingDebounceTime),
    [checkVideoStatus]
  );

  const checkVideoOnExitComponent = useCallback(
    () => checkVideoStatus(realtimeInputUrl)
      .finally(editorStatusLoadingWrapperForPromise(dispatchEditorStatus))
    , [checkVideoStatus, dispatchEditorStatus, realtimeInputUrl]
  );

  useEffect(() => {
    dispatchEditorStatus({
      type: 'SET_ON_EXIT_COMPONENT_EDITING_EFFECT',
      payload: checkVideoOnExitComponent,
    });
  }, [checkVideoOnExitComponent, dispatchEditorStatus]);

  const onChange = (property, { value }) => {
    // We don't update video url instantly, we do check in useEffect first.
    if (property === dataProperties.videoUrl) {
      const trimmedUrl = value.trim();
      debouncedCheckVideoStatus(trimmedUrl);
      setRealtimeInputUrl(trimmedUrl);
      // getErrors() read SiteConfig to get current video url error,
      // because video url and isValid flag in SiteConfig has a delay due to debounce,
      // we set videoCanPlay to true to overwrite isValid flag to update getError()'s return value.
      // and this state will be update to what it should be after checkVideoStatus().
      setVideoCanPlay(true);

      return;
    }

    const overwriteConfig = {
      [property]: value,
    };

    updateSiteConfig(overwriteConfig);
  };

  const errors = useMemo(() => getErrors(
    {
      ...videoConfigRef.current,
      [dataProperties.videoUrl]: realtimeInputUrl,
      [dataProperties.videoCanPlay]: videoCanPlay,
    },
    i18n
  ), [i18n, realtimeInputUrl, videoCanPlay]);

  const onDimensionChange = useCallback((ev, option) => {
    const sizeOption = option.key || VideoSize.SMALL;
    const overwriteConfig = {
      [dataProperties.videoWidth]: VideoSize2Px[sizeOption].width,
      [dataProperties.videoHeight]: VideoSize2Px[sizeOption].height,
    };

    updateSiteConfig(overwriteConfig);
  }, [updateSiteConfig]);

  const selectedVideoSize = useMemo(() => {
    const videoWidth = videoConfigRef.current[dataProperties.videoWidth];
    const videoHeight = videoConfigRef.current[dataProperties.videoHeight];

    const selectedKey = findKey(VideoSize2Px, ({ width, height }) => (
      width === videoWidth && height === videoHeight));

    if (!selectedKey) {
      // we need to reset width and height to default
      // if video section is created or not match our dimension.
      videoConfigRef.current[dataProperties.videoWidth] = 640;
      videoConfigRef.current[dataProperties.videoHeight] = 360;

      return VideoSize.SMALL;
    }

    return selectedKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoConfigRef.current]);

  const serializeValue =
    useCallback(value => truncateStringEndWithEmoji(value, DescriptionHardLimit), []);

  const videoSizeOptions = useMemo(() => map(videoSizeOption, option => ({
    ...option,
    text: i18n.getString(option.text),
  })), [videoSizeOption]);

  return (
    <div className="smart-page-embed-video-editor">
      <h2 className={titleClassName}>
        {i18n.getString(_TL_('Video'))}
      </h2>
      <div className={descriptionClassName}>
        {i18n.getString(_TL_('Add a video to your Smart Page (optional).'))}
      </div>
      <div className={editorContentClassName}>
        <div
          className="embed-video-url-editor"
          data-editable-property={dataProperties.videoUrl}
        >
          <SmartTextEditor
            isRequired
            label={i18n.getString(_TL_('YouTube or Vimeo URL'))}
            onChange={partial(onChange, dataProperties.videoUrl)}
            value={isEmpty(videoConfigRef.current) ? '' : videoConfigRef.current[dataProperties.videoUrl]}
            errorMessage={errors[dataProperties.videoUrl]}
            placeholder={i18n.getString(_TL_('e.g. youtube.com/test-youtube-url'))}
          />
        </div>
        <div data-editable-property={dataProperties.videoSize}>
          <Label className={sectionTitleClassName} >
            <span>{i18n.getString(_TL_('Video size(Desktop)'))}</span>
          </Label>
          <ChoiceGroup
            selectedKey={selectedVideoSize}
            options={videoSizeOptions}
            onChange={onDimensionChange}
            className={choiceGroupClassName}
          />
        </div>
        <div
          className="embed-video-description-editor"
          data-editable-property={dataProperties.videoDescription}
        >
          <SmartTextEditor
            label={i18n.getString(_TL_('Description'))}
            onChange={partial(onChange, dataProperties.videoDescription)}
            value={isEmpty(videoConfigRef.current) ? '' : videoConfigRef.current[dataProperties.videoDescription]}
            errorMessage={errors[dataProperties.videoDescription]}
            maxCharCount={DescriptionHardLimit}
            serializeValue={serializeValue}
          />
        </div>
      </div>
    </div>
  );
};

SmartVideoEditor.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
};
